import { Check, FileText, Info } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { ReactNode } from "react";

type PackageValidationStepProps = {
  labels: SafecheckTranslations["packages"]["create"]["validation"];
  statusLabels: SafecheckTranslations["packages"]["create"]["statuses"];
  hospital: string;
  company: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: "draft" | "active" | "suspended";
  includedActIds: string[];
  totalActs: number;
};

function formatPeriod(start: string, end: string, separator: string) {
  if (!start && !end) return "—";
  if (!start) return end;
  if (!end) return start;
  return `${start} ${separator} ${end}`;
}

function SummaryItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</p>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  );
}

export function PackageValidationStep({
  labels,
  statusLabels,
  hospital,
  company,
  name,
  periodStart,
  periodEnd,
  status,
  includedActIds,
  totalActs,
}: PackageValidationStepProps) {
  const includedCount = includedActIds.length;
  const outOfPackageCount = Math.max(totalActs - includedCount, 0);

  const statusBadgeClass =
    status === "active"
      ? "bg-teal-soft text-teal"
      : status === "suspended"
        ? "bg-pink-soft text-pink"
        : "bg-card-muted text-muted-foreground";

  const statusLabel =
    status === "draft"
      ? labels.statusDraft
      : status === "active"
        ? statusLabels.active.toUpperCase()
        : statusLabels.suspended.toUpperCase();

  return (
    <div>
      <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Check size={16} className="text-green" />
        {labels.title}
      </div>

      <div className="mb-5 rounded-xl border border-green/30 bg-green/10 px-4 py-3 text-sm text-foreground">
        {labels.readyMessage}
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-purple-soft px-4 py-3">
          <FileText size={16} className="text-purple" />
          <h3 className="text-sm font-semibold text-foreground">{labels.summary}</h3>
        </div>

        <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
          <SummaryItem label={labels.fields.hospital} value={hospital || "—"} />
          <SummaryItem label={labels.fields.company} value={company || "—"} />
          <SummaryItem label={labels.fields.packageName} value={name || "—"} />
          <SummaryItem
            label={labels.fields.period}
            value={formatPeriod(periodStart, periodEnd, labels.periodSeparator)}
          />
          <SummaryItem
            label={labels.fields.status}
            value={
              <span
                className={cn(
                  "inline-flex rounded-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
                  statusBadgeClass,
                )}
              >
                {statusLabel}
              </span>
            }
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-3 text-sm font-medium text-foreground">{labels.statistics}</p>
        <div className="flex flex-wrap gap-3">
          <span className="inline-flex rounded-md bg-teal-soft px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-green">
            {labels.includedActsBadge.replace("{count}", String(includedCount))}
          </span>
          <span className="inline-flex rounded-md bg-yellow-soft px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-yellow">
            {labels.outOfPackageBadge.replace("{count}", String(outOfPackageCount))}
          </span>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-xl border border-yellow/30 bg-yellow-soft/60 px-4 py-3 text-sm text-foreground">
        <Info size={16} className="mt-0.5 shrink-0 text-yellow" />
        <p>{labels.importantNotice}</p>
      </div>
    </div>
  );
}
